/**
 * Curated public showcase content for discovery pages.
 * Rendered locally — never written to Supabase unless intentionally seeded.
 */

import { DEMO_IDENTITIES } from '@/lib/demo-identities';

export type ShowcaseLabel =
  | 'Showcase'
  | 'Featured Archive'
  | 'Editorial Pick'
  | 'Community Spotlight'
  | 'Public Archive Example'
  | 'Ertzyx Showcase'
  | 'Verified Archive'
  | 'Showcase Article';

export type ArticleCategory =
  | 'Legacy'
  | 'Family Archive'
  | 'Community Heritage'
  | 'Public Proof'
  | 'Storytelling'
  | 'Witnessing'
  | 'Identity'
  | 'Collections';

export interface ShowcaseStory {
  id: string;
  title: string;
  body: string;
  pillar_tag: string;
  content_type: string;
  author_name: string;
  author_handle: string | null;
  author_avatar: string | null;
  source_type?: string | null;
  source_achievement_id?: string | null;
  witness_count: number;
  created_at: string;
  showcase_label: ShowcaseLabel;
  media_url?: string | null;
}

export interface ShowcasePerson {
  id: string;
  full_name: string;
  public_handle: string;
  subtitle: string;
  avatar_url: string | null;
  record_count: number;
  witness_count: number;
  showcase_label: ShowcaseLabel;
  kind?: 'individual' | 'institution';
}

export interface ShowcaseCollection {
  id: string;
  title: string;
  description: string;
  category: string;
  record_count: number;
  witness_count: number;
  icon_bg: string;
  accent: string;
  showcase_label: ShowcaseLabel;
  recent_titles: string[];
}

export interface ShowcaseCommunityPost {
  id: string;
  author_name: string;
  author_initials: string;
  avatar_color: string;
  author_avatar_url: string;
  author_type: 'Individual' | 'Institution';
  pillar_tag: string;
  story: string;
  witness_count: number;
  comments_count: number;
  endorsements: number;
  created_at: string;
  is_verified: boolean;
  showcase_label: ShowcaseLabel;
}

export interface ShowcaseArticle {
  id: string;
  slug: string;
  title: string;
  category: ArticleCategory;
  cover_image?: string | null;
  excerpt: string;
  body: string;
  reading_time: string;
  author: string;
  published_at: string;
  showcase_label: ShowcaseLabel;
}

export interface ShowcaseWitnessRequest {
  id: string;
  title: string;
  description: string;
  requester: string;
  pillar_tag: string;
  responses: number;
  showcase_label: ShowcaseLabel;
}

export interface ShowcaseHeritageHighlight {
  id: string;
  title: string;
  location: string;
  description: string;
  contributors: number;
  verified: boolean;
  showcase_label: ShowcaseLabel;
}

export interface ShowcaseProofExample {
  id: string;
  title: string;
  category: string;
  issuer: string;
  witness_count: number;
  verified: boolean;
  showcase_label: ShowcaseLabel;
}

const U = (id: string) => `https://images.unsplash.com/${id}?auto=format&fit=crop&w=160&h=160&q=80`;

const ARTICLE_COVER = (id: string) =>
  `https://images.unsplash.com/${id}?auto=format&fit=crop&w=1400&q=82`;

const daysAgo = (n: number) => new Date(Date.now() - n * 86400000).toISOString();

export const SHOWCASE_ARCHIVE_STATS = {
  contributors: 2847,
  public_stories: 12430,
  verified_records: 3890,
  witness_verifications: 21370,
  integrity: '100%',
};

export const SHOWCASE_STORIES: ShowcaseStory[] = [
  {
    id: 'showcase-story-001',
    title: 'Crossing three continents in one lifetime',
    body: 'When my grandmother left Port of Spain in 1962, she carried a single suitcase and a notebook of names she was determined not to forget. This archive traces her route through London, Toronto, and Vancouver — each stop documented with letters, photographs, and witness accounts from cousins who remember the rooms she rented and the jobs she took to send money home.',
    pillar_tag: 'History',
    content_type: 'written',
    author_name: 'Amara Okonkwo',
    author_handle: null,
    author_avatar: DEMO_IDENTITIES.amaraOkonkwo.avatar,
    witness_count: 34,
    created_at: daysAgo(2),
    showcase_label: 'Editorial Pick',
  },
  {
    id: 'showcase-story-002',
    title: 'The relay that almost did not happen',
    body: 'Athens, 1988. Our national federation had lost the original qualifying documents. Four of us reunited fifty years later to reconstruct the race — split times, lane assignments, and the coach who believed we could still make the final. Three teammates submitted witness records; the fourth contributed a photograph from a journalist who never published it.',
    pillar_tag: 'Performance',
    content_type: 'written',
    author_name: 'James Okafor',
    author_handle: null,
    author_avatar: U('photo-1507003211169-0a1dd7228f2d'),
    witness_count: 11,
    created_at: daysAgo(5),
    showcase_label: 'Community Spotlight',
  },
  {
    id: 'showcase-story-003',
    title: "Grandmother's kitchen as a classroom",
    body: 'Every Sunday for forty years, my grandmother taught three generations to cook the same twelve dishes. We recorded her voice explaining why she never measured the salt, photographed each step, and linked the recipes to migration dates on our family timeline. The archive is now a shared collection across seven cousins.',
    pillar_tag: 'History',
    content_type: 'written',
    author_name: 'Kenji Yamamoto',
    author_handle: null,
    author_avatar: DEMO_IDENTITIES.kenjiYamamoto.avatar,
    witness_count: 28,
    created_at: daysAgo(8),
    showcase_label: 'Featured Archive',
  },
  {
    id: 'showcase-story-004',
    title: 'First winter in Canada',
    body: 'Documenting arrival, language milestones, and the first community winter festival — from the perspective of a family that had never seen snow. Includes school report cards, a photo of borrowed coats, and a witness statement from the neighbour who taught us to shovel the driveway.',
    pillar_tag: 'Achievement',
    content_type: 'written',
    author_name: 'Sophie Lin',
    author_handle: null,
    author_avatar: DEMO_IDENTITIES.sophieLin.avatar,
    witness_count: 19,
    created_at: daysAgo(12),
    showcase_label: 'Ertzyx Showcase',
  },
  {
    id: 'showcase-story-005',
    title: 'The barber who sheltered families during the riots',
    body: 'Emmanuel Kweku ran a barbershop on Railton Road for thirty-one years. When the council voted to rename the park after him, we began building a formal heritage archive — oral histories from customers, photographs from the shop window, and council minutes that record the unanimous vote.',
    pillar_tag: 'Hero',
    content_type: 'written',
    author_name: 'Councillor James Adebayo',
    author_handle: null,
    author_avatar: U('photo-1472099645785-5658abf4ff4e'),
    witness_count: 56,
    created_at: daysAgo(3),
    showcase_label: 'Community Spotlight',
  },
  {
    id: 'showcase-story-006',
    title: '1967 Omega Seamaster — naval provenance chain',
    body: "Acquired at auction with a gap in ownership between 1940 and 1944. Over six months, three collectors and a maritime historian helped reconstruct the chain from a Royal Navy mess record to a Swiss dealer's ledger. Each link is a separate proof record with independent witnesses.",
    pillar_tag: 'Favorites / Products',
    content_type: 'written',
    author_name: 'Marco Valentini',
    author_handle: null,
    author_avatar: U('photo-1560250097-0b93528c311a'),
    witness_count: 7,
    created_at: daysAgo(6),
    showcase_label: 'Public Archive Example',
  },
];

export const SHOWCASE_ORAL_STORIES: ShowcaseStory[] = [
  {
    id: 'showcase-oral-001',
    title: 'Four generations at the Yellow River crossing',
    body: "Our family has passed down an oral tradition about crossing the Yellow River during the 1911 revolution. This recording captures my uncle's version — the names, the boats, and the cousin who stayed behind. Three siblings listened and added corrections in separate witness notes.",
    pillar_tag: 'History',
    content_type: 'voice_note',
    author_name: 'Wei Chen',
    author_handle: null,
    author_avatar: U('photo-1506794778202-cad84cf45f1d'),
    witness_count: 21,
    created_at: daysAgo(4),
    showcase_label: 'Featured Archive',
  },
  {
    id: 'showcase-oral-002',
    title: "Elders' memory circle — Session 12",
    body: 'Facilitated circle with consent-based publishing. Twelve elders share stories of the neighbourhood before the highway was built. Institution-reviewed summaries accompany each full recording.',
    pillar_tag: 'History',
    content_type: 'podcast',
    author_name: 'Vancouver Heritage Lab',
    author_handle: null,
    author_avatar: U('photo-1560250097-0b93528c311a'),
    witness_count: 44,
    created_at: daysAgo(9),
    showcase_label: 'Ertzyx Showcase',
  },
  {
    id: 'showcase-oral-003',
    title: 'The Blue Lantern Jazz Club, 1961–1979',
    body: 'Rosa Gutierrez recorded oral histories from musicians, regulars, and family members who remember the club before it closed. This excerpt covers the opening night and the saxophonist who played there every Friday for eleven years.',
    pillar_tag: 'History',
    content_type: 'voice_note',
    author_name: 'Rosa Gutierrez',
    author_handle: null,
    author_avatar: U('photo-1580489944761-15a19d654956'),
    witness_count: 22,
    created_at: daysAgo(14),
    showcase_label: 'Community Spotlight',
  },
];

export const SHOWCASE_PEOPLE: ShowcasePerson[] = [
  {
    id: 'showcase-person-001',
    full_name: DEMO_IDENTITIES.sophieLin.name,
    public_handle: 'sophie-lin',
    subtitle: DEMO_IDENTITIES.sophieLin.subtitle,
    avatar_url: DEMO_IDENTITIES.sophieLin.avatar,
    record_count: 47,
    witness_count: 128,
    showcase_label: 'Community Spotlight',
    kind: 'individual',
  },
  {
    id: 'showcase-person-002',
    full_name: DEMO_IDENTITIES.amaraOkonkwo.name,
    public_handle: 'amara-okonkwo',
    subtitle: DEMO_IDENTITIES.amaraOkonkwo.subtitle,
    avatar_url: DEMO_IDENTITIES.amaraOkonkwo.avatar,
    record_count: 62,
    witness_count: 89,
    showcase_label: 'Featured Archive',
    kind: 'individual',
  },
  {
    id: 'showcase-person-003',
    full_name: DEMO_IDENTITIES.kenjiYamamoto.name,
    public_handle: 'kenji-yamamoto',
    subtitle: DEMO_IDENTITIES.kenjiYamamoto.subtitle,
    avatar_url: DEMO_IDENTITIES.kenjiYamamoto.avatar,
    record_count: 31,
    witness_count: 74,
    showcase_label: 'Editorial Pick',
    kind: 'individual',
  },
  {
    id: 'showcase-person-004',
    full_name: DEMO_IDENTITIES.pacificHeritageMuseum.name,
    public_handle: 'pacific-heritage-museum',
    subtitle: DEMO_IDENTITIES.pacificHeritageMuseum.subtitle,
    avatar_url: DEMO_IDENTITIES.pacificHeritageMuseum.avatar,
    record_count: 28,
    witness_count: 41,
    showcase_label: 'Public Archive Example',
    kind: 'institution',
  },
  {
    id: 'showcase-person-005',
    full_name: DEMO_IDENTITIES.vancouverHeritageLab.name,
    public_handle: 'vancouver-heritage-lab',
    subtitle: DEMO_IDENTITIES.vancouverHeritageLab.subtitle,
    avatar_url: DEMO_IDENTITIES.vancouverHeritageLab.avatar,
    record_count: 19,
    witness_count: 52,
    showcase_label: 'Ertzyx Showcase',
    kind: 'institution',
  },
  {
    id: 'showcase-person-006',
    full_name: DEMO_IDENTITIES.coastalMemoryArchive.name,
    public_handle: 'coastal-memory-archive',
    subtitle: DEMO_IDENTITIES.coastalMemoryArchive.subtitle,
    avatar_url: DEMO_IDENTITIES.coastalMemoryArchive.avatar,
    record_count: 24,
    witness_count: 67,
    showcase_label: 'Showcase',
    kind: 'institution',
  },
];

export const SHOWCASE_COLLECTIONS: ShowcaseCollection[] = [
  {
    id: 'showcase-col-001',
    title: 'Migration Routes & Arrival Stories',
    description:
      'Personal histories, border crossings, and generational movement documented with witnesses and primary sources.',
    category: 'Cultural Archive',
    record_count: 342,
    witness_count: 891,
    icon_bg: '#faeeda',
    accent: '#854f0b',
    showcase_label: 'Featured Archive',
    recent_titles: [
      'Accra to London, 1962–1968',
      'Ellis Island arrival letter, 1923',
      'First winter in Canada',
    ],
  },
  {
    id: 'showcase-col-002',
    title: 'Local Heroes & Neighbourhood Memory',
    description:
      'Community historians preserving the people and places mainstream archives overlook.',
    category: 'Community Heritage',
    record_count: 187,
    witness_count: 534,
    icon_bg: '#e1f5ee',
    accent: '#0f6e56',
    showcase_label: 'Community Spotlight',
    recent_titles: [
      'Emmanuel Kweku, 1987 shelter hero',
      'The Blue Lantern Jazz Club',
      'Makoko Floating School founding',
    ],
  },
  {
    id: 'showcase-col-003',
    title: 'Provenance & Collectible Archives',
    description:
      'Ownership chains, authentication notes, and the stories behind rare and handmade objects.',
    category: 'Curated Collection',
    record_count: 156,
    witness_count: 312,
    icon_bg: '#eeedfe',
    accent: '#534ab7',
    showcase_label: 'Public Archive Example',
    recent_titles: [
      '1967 Omega Seamaster naval chain',
      'Braque lithograph restitution trail',
      'Bernard Leach studio pottery, 1952',
    ],
  },
  {
    id: 'showcase-col-004',
    title: 'Career Legacy & Professional Milestones',
    description:
      'Retiring professionals and alumni preserving achievements before institutional memory fades.',
    category: 'Verified Archive',
    record_count: 421,
    witness_count: 1024,
    icon_bg: '#e6f1fb',
    accent: '#185fa5',
    showcase_label: 'Ertzyx Showcase',
    recent_titles: [
      'Stanford NICU expansion, 1993–2001',
      'Olympic relay qualifying, Athens 1988',
      '28-year teaching career archive',
    ],
  },
];

export const SHOWCASE_COMMUNITY_POSTS: ShowcaseCommunityPost[] = [
  {
    id: 'showcase-post-001',
    author_name: DEMO_IDENTITIES.sophieLin.name,
    author_initials: 'SL',
    avatar_color: 'linear-gradient(135deg,#0f6e56,#5dcaa5)',
    author_avatar_url: DEMO_IDENTITIES.sophieLin.avatar,
    author_type: 'Individual',
    pillar_tag: 'Achievement',
    story:
      'I documented my first winter in Canada with arrival milestones, language notes, and the community festival that made Vancouver feel like home.',
    witness_count: 56,
    comments_count: 23,
    endorsements: 41,
    created_at: daysAgo(2),
    is_verified: true,
    showcase_label: 'Community Spotlight',
  },
  {
    id: 'showcase-post-002',
    author_name: DEMO_IDENTITIES.amaraOkonkwo.name,
    author_initials: 'AO',
    avatar_color: 'linear-gradient(135deg,#854f0b,#ef9f27)',
    author_avatar_url: DEMO_IDENTITIES.amaraOkonkwo.avatar,
    author_type: 'Individual',
    pillar_tag: 'Hero',
    story:
      'Our neighborhood care network documented supply runs and wellness check-ins during the winter storm, with consent-based summaries from every household involved.',
    witness_count: 8,
    comments_count: 14,
    endorsements: 6,
    created_at: daysAgo(1),
    is_verified: false,
    showcase_label: 'Featured Archive',
  },
  {
    id: 'showcase-post-003',
    author_name: DEMO_IDENTITIES.kenjiYamamoto.name,
    author_initials: 'KY',
    avatar_color: 'linear-gradient(135deg,#1d4ed8,#60a5fa)',
    author_avatar_url: DEMO_IDENTITIES.kenjiYamamoto.avatar,
    author_type: 'Individual',
    pillar_tag: 'History',
    story:
      'We preserved my grandmother’s kitchen lessons through recipe cards, oral history clips, and intergenerational teaching moments shared across our family archive.',
    witness_count: 44,
    comments_count: 31,
    endorsements: 28,
    created_at: daysAgo(5),
    is_verified: false,
    showcase_label: 'Editorial Pick',
  },
  {
    id: 'showcase-post-004',
    author_name: DEMO_IDENTITIES.pacificHeritageMuseum.name,
    author_initials: 'PH',
    avatar_color: 'linear-gradient(135deg,#7c3aed,#c084fc)',
    author_avatar_url: DEMO_IDENTITIES.pacificHeritageMuseum.avatar,
    author_type: 'Institution',
    pillar_tag: 'History',
    story:
      'Our newcomer listening booth now has privacy-safe public summaries, while full recordings remain protected in the institutional archive.',
    witness_count: 31,
    comments_count: 19,
    endorsements: 22,
    created_at: daysAgo(4),
    is_verified: true,
    showcase_label: 'Public Archive Example',
  },
];

export const SHOWCASE_WITNESS_REQUESTS: ShowcaseWitnessRequest[] = [
  {
    id: 'showcase-witness-001',
    title: '1988 Olympic relay teammates needed',
    description:
      'Seeking three teammates to verify our relay qualifying record. National athletics federation lost the original documents.',
    requester: 'James Okafor',
    pillar_tag: 'Performance',
    responses: 4,
    showcase_label: 'Community Spotlight',
  },
  {
    id: 'showcase-witness-002',
    title: 'Identify family in Labé region photograph',
    description:
      'Oral accounts from great-grandmother mention a Fula family who emigrated to Senegal around 1940. Seeking genealogists familiar with the region.',
    requester: 'Amara Diallo',
    pillar_tag: 'History',
    responses: 14,
    showcase_label: 'Featured Archive',
  },
  {
    id: 'showcase-witness-003',
    title: 'Blue Lantern Jazz Club regulars',
    description:
      'Looking for musicians, regulars, or family members who can contribute oral histories of the club from 1961 to 1979.',
    requester: 'Rosa Gutierrez',
    pillar_tag: 'History',
    responses: 22,
    showcase_label: 'Ertzyx Showcase',
  },
];

export const SHOWCASE_HERITAGE: ShowcaseHeritageHighlight[] = [
  {
    id: 'showcase-heritage-001',
    title: 'Emmanuel Kweku Heritage Park',
    location: 'Brixton, London',
    description:
      'Community archive documenting the barber who sheltered families during the 1987 riots. Council renaming approved; formal record in progress.',
    contributors: 56,
    verified: true,
    showcase_label: 'Community Spotlight',
  },
  {
    id: 'showcase-heritage-002',
    title: 'The Blue Lantern Jazz Club',
    location: 'Harlem, New York',
    description:
      'Oral histories from musicians and regulars preserving a neighbourhood institution that operated from 1961 to 1979.',
    contributors: 22,
    verified: false,
    showcase_label: 'Featured Archive',
  },
  {
    id: 'showcase-heritage-003',
    title: 'Makoko Floating School',
    location: 'Lagos, Nigeria',
    description:
      'Multi-contributor community heritage record documenting the founding and impact of the floating school project.',
    contributors: 17,
    verified: true,
    showcase_label: 'Public Archive Example',
  },
];

export const SHOWCASE_PROOF: ShowcaseProofExample[] = [
  {
    id: 'showcase-proof-001',
    title: 'Stanford NICU Expansion Lead Role',
    category: 'Career Legacy',
    issuer: 'Stanford Medical Center',
    witness_count: 32,
    verified: true,
    showcase_label: 'Verified Archive',
  },
  {
    id: 'showcase-proof-002',
    title: '1967 Omega Seamaster Provenance Chain',
    category: 'Collectible Record',
    issuer: 'Collector Network',
    witness_count: 7,
    verified: true,
    showcase_label: 'Public Archive Example',
  },
  {
    id: 'showcase-proof-003',
    title: 'Graduation Journey Wall — Cohort 2024',
    category: 'Institutional Record',
    issuer: 'Coastal Memory Archive',
    witness_count: 18,
    verified: true,
    showcase_label: 'Ertzyx Showcase',
  },
];

export const SHOWCASE_ARTICLES: ShowcaseArticle[] = [
  {
    id: 'showcase-article-001',
    slug: 'preserve-family-migration-story',
    title: 'How to Preserve a Family Migration Story',
    category: 'Family Archive',
    cover_image: ARTICLE_COVER('photo-1529156069898-49953e39b3ac'),
    excerpt:
      'Migration stories disappear when no one records the route, the reasons, and the witnesses who remember the crossing. Here is a practical framework for building a permanent family migration archive on Ertzyx.',
    reading_time: '8 min read',
    author: 'Ertzyx Editorial',
    published_at: daysAgo(21),
    showcase_label: 'Showcase Article',
    body: `Migration is rarely a single event. It is a chain of decisions — who left, who stayed, what was carried, what was left behind, and who helped along the way. On Ertzyx, a migration story becomes durable when it is structured as a record with witnesses, not just a post that fades from a feed.

**Start with the route.** Map each stop: departure city, transit points, arrival, and any return visits. Link photographs, letters, and oral recordings to specific dates on your Journey Map. A cousin in London and an aunt in Toronto can each witness the same arrival letter from different angles.

**Separate story from record.** A story captures voice, emotion, and narrative. A record captures what can be verified: dates, documents, names, locations. Migration archives work best when both exist — the oral history for texture, the record for integrity.

**Invite witnesses early.** The people who shared a room, signed a lease, or met you at the airport are not optional extras. They are the integrity layer. Send witness requests while memories are fresh, not after someone passes.

**Use collections.** Group migration documents under a single collection — "Osei Family, Accra to London 1962–1968" — so future relatives inherit a coherent archive rather than scattered posts.

**Publish when ready.** Private family work can stay in Family Ledger until you choose to share. Public migration stories help others recognize their own journeys and connect across diaspora networks.`,
  },
  {
    id: 'showcase-article-002',
    slug: 'why-witnessed-memories-matter',
    title: 'Why Witnessed Memories Matter',
    category: 'Witnessing',
    cover_image: ARTICLE_COVER('photo-1517048676732-d65bc937f952'),
    excerpt:
      'A memory alone is fragile. A witnessed memory becomes part of a shared record that others can trust, reference, and build upon.',
    reading_time: '6 min read',
    author: 'Ertzyx Editorial',
    published_at: daysAgo(18),
    showcase_label: 'Showcase Article',
    body: `Every family has stories that two people remember differently. Every community has heroes that only a few neighbours can confirm. Witnessing is how Ertzyx turns personal recollection into collective credibility.

**Witnessing is not voting.** You are not asked to agree with every detail. You confirm what you were present for, what you saw, or what you can independently verify from documents you hold.

**Witness chains strengthen over time.** One witness makes a story plausible. Three witnesses with independent connections make it durable. Institutions can issue proof records on top of witness chains when verification standards are met.

**Witness requests are invitations.** When someone asks "were you there?", they are building an archive that includes you. Responding takes minutes; the record lasts generations.

**Public witnessing builds community.** On Pulse and Community, witness counts signal which stories the network finds credible enough to endorse — not algorithmically, but humanly.`,
  },
  {
    id: 'showcase-article-003',
    slug: 'story-vs-record',
    title: 'The Difference Between a Story and a Record',
    category: 'Storytelling',
    cover_image: ARTICLE_COVER('photo-1455390582262-044cdead277a'),
    excerpt:
      'Stories move people. Records preserve truth. Ertzyx is designed for both — and understanding the difference helps you archive with intention.',
    reading_time: '7 min read',
    author: 'Ertzyx Editorial',
    published_at: daysAgo(15),
    showcase_label: 'Showcase Article',
    body: `A story is narrative: voice, perspective, emotion, the way someone experienced an event. A record is structural: dates, names, documents, witness statements, verification status.

**Stories belong in Story Hub and Pulse.** They are how you share humanity — the fear before a performance, the joy of a reunion, the texture of a grandmother's kitchen.

**Records belong in your vault, Family Ledger, and proof cards.** They are how you preserve what can be checked — graduation dates, employment milestones, ownership chains, council minutes.

**The handoff matters.** Ertzyx lets you convert a story into a record when you are ready to add witnesses and structure. A Pulse post about a neighbourhood hero can become a Community Heritage record with council documents attached.

**Neither replaces the other.** A record without story feels cold. A story without record feels temporary. The best public archives on Ertzyx combine both.`,
  },
  {
    id: 'showcase-article-004',
    slug: 'building-public-legacy-profile',
    title: 'Building a Public Legacy Profile',
    category: 'Identity',
    cover_image: ARTICLE_COVER('photo-1497366754035-f200968a6e72'),
    excerpt:
      'Your public profile is not a social media page. It is a curated entry point to your verified records, stories, and collections.',
    reading_time: '9 min read',
    author: 'Ertzyx Editorial',
    published_at: daysAgo(12),
    showcase_label: 'Showcase Article',
    body: `A legacy profile on Ertzyx answers one question: what do you want the world to remember about you, and what can you prove?

**Choose visibility deliberately.** Public Identity lets you expose your vault, journey map, life cards, and selected stories — while keeping private journal entries and family-only records hidden.

**Lead with proof.** Visitors trust profiles that show verified records and witness counts, not just biographical text. Pin your strongest proof cards and institutional credentials.

**Collections tell career arcs.** Alumni and professionals use collections to group decades of achievement — publications, roles, athletic milestones — into browsable archives.

**Handles are permanent addresses.** Your @handle and ERV ID become how witnesses, institutions, and family find you across decades. Choose something you are willing to carry.

**Update intentionally.** A legacy profile is living, but not ephemeral. Add records when milestones happen; revise your public narrative when your story changes.`,
  },
  {
    id: 'showcase-article-005',
    slug: 'communities-archive-local-heroes',
    title: 'How Communities Can Archive Local Heroes',
    category: 'Community Heritage',
    cover_image: ARTICLE_COVER('photo-1517486808906-6ca8b3f04846'),
    excerpt:
      'Mainstream history misses most neighbourhood heroes. Community historians on Ertzyx are building the archives institutions never will.',
    reading_time: '10 min read',
    author: 'Ertzyx Editorial',
    published_at: daysAgo(9),
    showcase_label: 'Showcase Article',
    body: `Local heroes — the barber who sheltered families, the teacher who stayed after school for thirty years, the shopkeeper who extended credit during hard winters — rarely appear in municipal archives. Community Heritage on Ertzyx is built for exactly these people.

**Start with oral histories.** Record neighbours while they remember. Consent matters: ask whether summaries, full audio, or both can be published.

**Link to place.** Add heritage highlights to your Geospatial Map so the story connects to a park, a shop, a street corner.

**Gather council and newspaper evidence.** When official recognition happens — a park renaming, a civic award — attach documents as proof records alongside oral histories.

**Use Circle Directory.** Community Historians circle connects local archivists who share methods, witness each other's work, and request help across neighbourhoods.

**Witness requests bring people in.** "Did you know Emmanuel?" is an invitation. Responses become part of the archive.`,
  },
  {
    id: 'showcase-article-006',
    slug: 'pulse-to-proof',
    title: 'From Pulse to Proof: How Ertzyx Turns Posts into Records',
    category: 'Public Proof',
    cover_image: ARTICLE_COVER('photo-1551288049-bebda4e38f71'),
    excerpt:
      'Pulse is the short public feed. Proof is the verified layer. The path between them is what makes Ertzyx different from social media.',
    reading_time: '8 min read',
    author: 'Ertzyx Editorial',
    published_at: daysAgo(6),
    showcase_label: 'Showcase Article',
    body: `Pulse is where witness updates, questions, and short stories appear in real time. It is human, social, and immediate. Proof is where those moments become durable — witnessed, structured, and optionally institution-verified.

**Step 1: Post to Pulse.** Share an update, ask for witnesses, or document something happening now.

**Step 2: Gather witnesses.** People who were there confirm details. Witness counts signal community credibility.

**Step 3: Convert to a record.** When a Pulse post has enough context and witnesses, convert it to a story or proof card with full metadata.

**Step 4: Institutional verification (optional).** Schools, employers, and heritage institutions can issue verified records that link back to your original post.

**The archive stays connected.** Every step preserves provenance — visitors can see how a casual update became a permanent record.`,
  },
  {
    id: 'showcase-article-007',
    slug: 'preserving-objects-provenance',
    title: 'Preserving Objects, Provenance, and Personal Meaning',
    category: 'Collections',
    cover_image: ARTICLE_COVER('photo-1513519245088-0e12902e5a38'),
    excerpt:
      'Objects carry histories. Collectors and creators on Ertzyx document ownership chains, authentication, and the meaning behind what they keep.',
    reading_time: '11 min read',
    author: 'Ertzyx Editorial',
    published_at: daysAgo(4),
    showcase_label: 'Showcase Article',
    body: `A watch, a first edition, a grandmother's kimono — each object has a chain of ownership and a story of why it mattered. Ertzyx Collection Vaults are designed for both.

**Document provenance gaps honestly.** Missing years do not disqualify an archive. They invite collaboration. Post witness requests to collector circles when you need expertise.

**Separate authentication from sentiment.** Expert verification notes belong in proof records. Personal meaning belongs in stories. Both can link to the same object.

**Photograph condition and markings.** Serial numbers, signatures, repair marks — details that seem minor become critical decades later.

**Join Collectors & Creators.** The Circle Directory community shares methods for textile classification, auction research, and vintage instrument verification.`,
  },
  {
    id: 'showcase-article-008',
    slug: 'oral-histories-public-memory',
    title: 'How Oral Histories Become Public Memory',
    category: 'Legacy',
    cover_image: ARTICLE_COVER('photo-1516280440614-37939bbacd81'),
    excerpt:
      'Voice recordings are among the most fragile and most precious archives. Ertzyx treats oral history as first-class public memory.',
    reading_time: '9 min read',
    author: 'Ertzyx Editorial',
    published_at: daysAgo(1),
    showcase_label: 'Showcase Article',
    body: `Oral histories disappear when devices fail, platforms shut down, or elders pass without recording. Ertzyx Oral Stories tab and voice content types exist to prevent that loss.

**Record with consent.** Ask whether audio can be public, circle-only, or family-private. Consent is part of the record metadata.

**Transcribe for search.** Voice is irreplaceable; text makes archives discoverable. Add summaries even when full audio is published.

**Invite corrective witnesses.** Siblings remember differently. Multiple witness notes on the same recording strengthen integrity without forcing false consensus.

**Link to collections.** Group oral histories by theme — "Grandmother's Kitchen", "Elders' Memory Circle" — so listeners find related voices.

**Institutions can review.** Heritage labs and museums can add reviewed summaries alongside community-submitted recordings, creating a trusted public layer.`,
  },
];

export const ARTICLE_CATEGORIES: ArticleCategory[] = [
  'Legacy',
  'Family Archive',
  'Community Heritage',
  'Public Proof',
  'Storytelling',
  'Witnessing',
  'Identity',
  'Collections',
];

/** Real items first, then showcase fill. */
export function mergeWithShowcase<T extends { id: string }>(real: T[], showcase: T[]): T[] {
  const realIds = new Set(real.map((r) => r.id));
  const extra = showcase.filter((s) => !realIds.has(s.id));
  return [...real, ...extra];
}

export function isShowcaseId(id: string): boolean {
  return id.startsWith('showcase-');
}

export interface ExploreStoryLike {
  id: string;
  user_id: string | null;
  title: string | null;
  body: string | null;
  pillar_tag: string | null;
  media_url: string | null;
  content_type: string | null;
  created_at: string;
  witness_count: number;
  author_name: string;
  author_handle: string | null;
  author_avatar: string | null;
  is_showcase?: boolean;
  showcase_label?: ShowcaseLabel;
}

export function mapShowcaseStory(s: ShowcaseStory): ExploreStoryLike {
  return {
    id: s.id,
    user_id: null,
    title: s.title,
    body: s.body,
    pillar_tag: s.pillar_tag,
    media_url: s.media_url ?? null,
    content_type: s.content_type,
    created_at: s.created_at,
    witness_count: s.witness_count,
    author_name: s.author_name,
    author_handle: s.author_handle,
    author_avatar: s.author_avatar,
    is_showcase: true,
    showcase_label: s.showcase_label,
  };
}

export function getShowcaseExploreStories(): ExploreStoryLike[] {
  return SHOWCASE_STORIES.map(mapShowcaseStory);
}

export function getShowcaseOralStories(): ExploreStoryLike[] {
  return SHOWCASE_ORAL_STORIES.map(mapShowcaseStory);
}

export function getArticleBySlug(slug: string): ShowcaseArticle | undefined {
  return SHOWCASE_ARTICLES.find((a) => a.slug === slug);
}
